import {Box, Button, FormLabel, SimpleGrid} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import React, {FC, useEffect} from 'react'
import {useFormContext, useWatch} from 'react-hook-form'
import Input from '_app/components/Form/Input'
import {PlacesAutocomplete, Radio} from '_app/components'
import {Country} from '_app/types/api'
import {getCountryById} from '../utils/regions'
import CountriesSelect from './CountriesSelect'
import RegionsSelect from './RegionsSelect'
import {nextRestClient, nextRestClientGUS} from '../../../helpers/ApiHelper/ApiHelper'
import {mapCompanyData} from '../utils/companyData'
import {useRouter} from 'next/router'
import toast from '../../../_app/utils/toast'

interface BillingAddressFormProps {
    countries: Country[];
}

const invoiceDifferentFields = ['firstname', 'lastname', 'company', 'vat_id']

const BillingAddressForm: FC<BillingAddressFormProps> = ({countries}) => {
    const {t} = useTranslation('checkout')
    const { locale } = useRouter()
    const {
        control,
        getValues,
        setValue,
        trigger,
        formState: {isValid, touchedFields},
    } = useFormContext()
    const billingCountry = useWatch({
        control,
        name: 'billing_address.country_code',
    })
    const selectedCountry = getCountryById(billingCountry, countries)
    const companyInvoice =
        useWatch({
            control,
            name: 'billing_address.invoice_type',
        }) === 'company'

    useEffect(() => {
        if (!isValid) {
            invoiceDifferentFields.forEach(field => {
                if (touchedFields.billing_address?.[field]) {
                    trigger(`billing_address.${field}`).then(() => {
                    })
                }
            })
        }
    }, [companyInvoice, trigger, touchedFields])

    const getCompanyData = async () => {
        const {vat_id} = getValues().billing_address
        if (!vat_id) return
        await nextRestClientGUS.get('', { key: process.env.NEXT_PRIVATE_GUS_KEY ?? '' } as any).then((res) => {
            const data = res?.data
            const companyData = data?.companyData
            if (companyData?.length > 0) {
                toast({description: t('COMPANY_DATA_NOT_FOUND'), status: 'error'})
            } else {
                mapCompanyData(companyData, setValue)
            }
        }).catch((error) => {
            toast({description: t('COMPANY_DATA_REQUEST_ERROR'), status: 'error'})
        })
    }

    return (
        <Box>
            <Radio
                name="billing_address.invoice_type"
                options={[
                    {label: t('INVOICE_NAME'), value: 'individual'},
                    {label: t('INVOICE_COMPANY'), value: 'company'},
                ]}
                style={{padding: '8px 20px'}}
                enableButtons
            />

            <Box mt={10} mb={10}>
                <>
                    <SimpleGrid
                        columns={{base: 1, md: 2}}
                        spacingX={8}
                        key="company"
                        display={companyInvoice ? 'grid' : 'none'}
                    >
                        <Input
                            label={t('TIN_LABEL')}
                            name="billing_address.vat_id"
                            placeholder={t('TIN_PLACEHOLDER')}
                            inputMode="numeric"
                            isRequired
                        />
                         {/* {*/}
                         {/*    locale === 'pl' && (*/}
                         {/*        <Button sx={{ marginTop: '20px' }} onClick={getCompanyData} marginBottom={{base: 6, md: 0}}>*/}
                         {/*            {t('GET_COMPANY_DATA')}*/}
                         {/*        </Button>*/}
                         {/*    )*/}
                         {/* }*/}
                        <Input
                            label={t('COMPANY_NAME_LABEL')}
                            name="billing_address.company"
                            placeholder={t('COMPANY_NAME_PLACEHOLDER')}
                            autoFocus
                            isRequired
                        />
                    </SimpleGrid>

                    <SimpleGrid
                        display={!companyInvoice ? 'grid' : 'none'}
                        columns={{base: 1, md: 2}}
                        spacingX={8}
                        key="personal"
                    >
                        <Input
                            isRequired
                            label={t('FIRST_NAME_LABEL')}
                            name="billing_address.firstname"
                            placeholder={t('FIRST_NAME_PLACEHOLDER')}
                            autoFocus
                        />
                        <Input
                            isRequired
                            label={t('LAST_NAME_LABEL')}
                            name="billing_address.lastname"
                            placeholder={t('LAST_NAME_PLACEHOLDER')}
                        />
                    </SimpleGrid>

                    <FormLabel as="p" my={8}>
                        {t('INVOICE_ADDRESS')}
                    </FormLabel>
                    <PlacesAutocomplete
                        name="billing_address.street[0]"
                        defaultValue={getValues().billing_address?.street?.[0]}
                        streetNumberKey="billing_address.street[1]"
                        countryKey="billing_address.country_code"
                        postcodeKey="billing_address.postcode"
                        regionKey="billing_address.region_id"
                        cityKey="billing_address.city"
                    />

                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={8}>
                        <Input
                            isRequired
                            label={t('HOUSE_NUMBER_LABEL')}
                            name="billing_address.street[1]"
                            placeholder={t('HOUSE_NUMBER_PLACEHOLDER')}
                            maxLength={7}
                        />
                        <Input
                            label={t('APARTMENT_NUMBER_LABEL')}
                            name="billing_address.street[2]"
                            placeholder={t('APARTMENT_NUMBER_PLACEHOLDER')}
                            maxLength={7}
                        />
                    </SimpleGrid>

                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={8}>
                        <Input
                            isRequired
                            label={t('CITY_LABEL')}
                            name="billing_address.city"
                            placeholder={t('CITY_PLACEHOLDER')}
                        />
                        <Input
                            isRequired
                            label={t('POSTCODE_LABEL')}
                            name="billing_address.postcode"
                            placeholder={t('POSTCODE_PLACEHOLDER')}
                        />
                    </SimpleGrid>

                    <RegionsSelect
                        country={selectedCountry}
                        name="billing_address.region_id"
                    />

                    <CountriesSelect
                        selectedCountry={selectedCountry}
                        name="billing_address.country_code"
                        shippingCode={companyInvoice ? 'PL' : undefined}
                        showAll={true}
                        isRequired
                    />
                </>
            </Box>
        </Box>
    )
}

export default BillingAddressForm
